import React, { useEffect } from 'react';
import { KeyboardAvoidingView, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import { radius, shadows, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet, sheetWidth } from '@/theme/responsive';

interface Props {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxHeightPct?: number;
  /** Scroll the content when it is taller than the sheet (a short phone, or with the keyboard up).
   *  Pass false when the content scrolls itself, e.g. a list above buttons that stay pinned. */
  scrollable?: boolean;
}

/** Lightweight bottom sheet built on Modal + Reanimated — used for filters, coupon entry,
 *  payment method pickers, and any other "pick from a list without leaving the screen" UI. */
export function BottomSheet({ visible, onClose, title, children, maxHeightPct = 0.85, scrollable = true }: Props) {
  const insets = useSafeAreaInsets();
  const progress = useSharedValue(0);
  const { colors } = useAppTheme();

  useEffect(() => {
    progress.value = withTiming(visible ? 1 : 0, { duration: 220 });
  }, [visible, progress]);

  const sheetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: (1 - progress.value) * 40 }],
    opacity: progress.value,
  }));
  const overlayStyle = useAnimatedStyle(() => ({ opacity: progress.value }));

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      {/* A Modal is its own window on Android, so the screen's keyboard handling doesn't reach it:
          without this the keyboard covered the whole sheet (a coupon code was typed blind). */}
      <KeyboardAvoidingView style={styles.root} behavior="padding">
        <Animated.View style={[StyleSheet.absoluteFill, overlayStyle]}>
          <Pressable
            style={[styles.overlay, { backgroundColor: colors.overlay }]}
            onPress={onClose}
            accessibilityLabel="Close"
            accessibilityRole="button"
          />
        </Animated.View>
        <Animated.View
          style={[
            styles.sheet,
            {
              backgroundColor: colors.surface,
              maxHeight: `${maxHeightPct * 100}%`,
              paddingBottom: insets.bottom + spacing.lg,
            },
            sheetStyle,
          ]}>
          <View style={[styles.handle, { backgroundColor: colors.border }]} />
          {title ? (
            <View style={styles.header}>
              <Text style={[type.h3, { color: colors.textPrimary, flexShrink: 1 }]}>{title}</Text>
              <Pressable accessibilityRole="button" accessibilityLabel="Close" onPress={onClose} hitSlop={10}>
                <Ionicons name="close" size={22} color={colors.textSecondary} />
              </Pressable>
            </View>
          ) : null}
          {scrollable ? (
            <ScrollView bounces={false} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              {children}
            </ScrollView>
          ) : (
            children
          )}
        </Animated.View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = ScaledSheet.create({
  root: { flex: 1, justifyContent: 'flex-end' },
  overlay: { flex: 1 },
  sheet: {
    ...sheetWidth,
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
    ...shadows.floating,
  },
  handle: { width: 40, height: 4, borderRadius: 2, alignSelf: 'center', marginBottom: spacing.md },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg },
});
